/**
 * Ein Spielplatz: Gerätebestand pflegen und eine Prüfung beginnen.
 *
 * Der Gerätebestand hängt am Platz, nicht an der Prüfung. Wer hier ein Gerät
 * anlegt, findet es in jeder künftigen Prüfung wieder.
 */

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useGeraetAendern,
  useGeraetAnlegen,
  useGeraetAusmustern,
  useGeraete,
  useGeraetetypen,
  usePruefungAnlegen,
  usePruefungen,
  useSpielplatz,
  useSpielplatzAendern,
} from '../api/abfragen'
import { PRUEFARTTEXT, type Geraet, type Pruefart } from '../api/typen'
import { Bearbeiten, type Feld } from '../bausteine/Bearbeiten'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { darfPruefen, darfSchreiben, useKonto } from '../konto/KontoKontext'
import { Einplanen } from '../bausteine/Einplanen'
import { NurPruefer } from '../bausteine/NurPruefer'

const PRUEFARTEN: Pruefart[] = [
  'hauptuntersuchung',
  'operativ',
  'abnahme',
  'nachpruefung',
]

export function SpielplatzDetail() {
  const { platzId = '' } = useParams()
  const { konto } = useKonto()
  const gehZu = useNavigate()

  const platz = useSpielplatz(platzId)
  const geraete = useGeraete(platzId)
  const typen = useGeraetetypen()
  const pruefungen = usePruefungen(undefined, platzId)
  const geraetAnlegen = useGeraetAnlegen(platzId)
  const ausmustern = useGeraetAusmustern(platzId)
  const platzAendern = useSpielplatzAendern()
  const geraetAendern = useGeraetAendern(platzId)
  const [platzBearbeiten, setzePlatzBearbeiten] = useState(false)
  const [geraetBearbeiten, setzeGeraetBearbeiten] = useState<Geraet | null>(null)
  const pruefungAnlegen = usePruefungAnlegen()

  const [geraeteformular, setzeGeraeteformular] = useState(false)
  const [pruefformular, setzePruefformular] = useState(false)
  const [einplanen, setzeEinplanen] = useState(false)

  if (platz.isLoading) return <Ladeanzeige />
  if (platz.error) return <Fehlerhinweis fehler={platz.error} />
  if (!platz.data) return <Leerhinweis titel="Spielplatz nicht gefunden" />

  const p = platz.data
  const laufend = (pruefungen.data ?? []).find((x) => x.status !== 'abgeschlossen')

  return (
    <div className="space-y-4">
      <nav className="text-base">
        <Link to={`/kunden/${p.kunde_id}`} className="text-marke-700 underline">
          ← {p.kunde_name}
        </Link>
      </nav>

      <header className="flex flex-wrap items-start gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{p.name}</h1>
          <p className="text-slate-700">
            {p.strasse ? `${p.strasse}, ` : ''}
            {p.plz} {p.ort}
          </p>
          {/* Die System-ID zum Nennen: eindeutig über Plätze und Geräte
              hinweg, also am Telefon ohne Zusatz brauchbar. Getrennt von
              der Nummer des Prüfbüros darüber. */}
          {p.kennung && (
            <p className="mt-1 text-base text-slate-600">
              System-ID <span className="font-semibold tabular-nums">{p.kennung}</span>
            </p>
          )}
        </div>
        {darfSchreiben(konto) && (
          <button
            type="button"
            className="knopf-still"
            onClick={() => setzePlatzBearbeiten(true)}
          >
            Bearbeiten
          </button>
        )}
      </header>

      <div className="karte grid grid-cols-2 gap-3 text-base sm:grid-cols-4">
        <div>
          <div className="font-semibold">{p.anzahl_geraete}</div>
          <div className="text-slate-600">Geräte</div>
        </div>
        <div>
          <div className="font-semibold">{datumDeutsch(p.letzte_pruefung_am)}</div>
          <div className="text-slate-600">zuletzt geprüft</div>
        </div>
        <div>
          <div className="font-semibold">{datumDeutsch(p.naechste_pruefung_am)}</div>
          <div className="text-slate-600">nächste Prüfung</div>
        </div>
        <div>
          <div className={`font-semibold ${p.offene_maengel > 0 ? 'text-stufe-3' : ''}`}>
            {p.offene_maengel}
          </div>
          <div className="text-slate-600">offene Mängel</div>
        </div>
      </div>

      {/* Prüfung beginnen oder fortsetzen. Wer nicht prüfen darf, bekommt
          nicht nichts zu sehen, sondern den Grund. */}
      {!darfPruefen(konto) && <NurPruefer was="Eine Prüfung zu beginnen" />}

      {darfPruefen(konto) && (
        <section className="karte space-y-3">
          <h2 className="text-xl font-bold">Prüfung</h2>

          {laufend ? (
            <>
              <p className="text-base text-slate-700">
                Es läuft bereits eine {PRUEFARTTEXT[laufend.pruefart]} vom{' '}
                {datumDeutsch(laufend.datum)}.
              </p>
              <Link to={`/pruefungen/${laufend.id}/bogen`} className="knopf-haupt">
                Prüfbogen öffnen
              </Link>
            </>
          ) : p.anzahl_geraete === 0 ? (
            <p className="text-base text-slate-700">
              Legen Sie zuerst den Gerätebestand an – der Prüfbogen entsteht daraus.
            </p>
          ) : !pruefformular ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="knopf-haupt"
                onClick={() => setzePruefformular(true)}
              >
                Jetzt prüfen
              </button>
              {/* Einplanen statt sofort prüfen: der häufigere Fall. Ohne
                  diesen Weg taucht ein Platz, der noch nie geprüft wurde,
                  nie unter „Fällig“ auf — eine Aufgabe entstand bisher nur
                  beim Abschluss einer Prüfung. */}
              <button
                type="button"
                className="knopf-zweit"
                onClick={() => setzeEinplanen(true)}
              >
                Prüfung einplanen
              </button>
            </div>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault()
                const f = new FormData(e.currentTarget)
                pruefungAnlegen.mutate(
                  {
                    spielplatz_id: platzId,
                    pruefart: String(f.get('pruefart')),
                    datum: String(f.get('datum')),
                    wetter: String(f.get('wetter') ?? '') || null,
                    temperatur_c: f.get('temperatur_c')
                      ? Number(f.get('temperatur_c'))
                      : null,
                    begleitung: String(f.get('begleitung') ?? '') || null,
                  },
                  { onSuccess: (neu) => gehZu(`/pruefungen/${neu.id}/bogen`) },
                )
              }}
            >
              <div>
                <label htmlFor="pruefart">Art der Prüfung</label>
                <select id="pruefart" name="pruefart" defaultValue="hauptuntersuchung">
                  {PRUEFARTEN.map((a) => (
                    <option key={a} value={a}>
                      {PRUEFARTTEXT[a]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label htmlFor="datum">Datum</label>
                  <input
                    id="datum"
                    name="datum"
                    type="date"
                    required
                    defaultValue={new Date().toISOString().slice(0, 10)}
                  />
                </div>
                <div>
                  <label htmlFor="wetter">Wetter</label>
                  <input id="wetter" name="wetter" placeholder="bedeckt, trocken" />
                </div>
                <div>
                  <label htmlFor="temperatur_c">Temperatur °C</label>
                  <input id="temperatur_c" name="temperatur_c" inputMode="numeric" />
                </div>
              </div>
              <div>
                <label htmlFor="begleitung">Begleitung vor Ort</label>
                <input id="begleitung" name="begleitung" placeholder="Herr Meyer, Bauhof" />
              </div>

              <Fehlerhinweis fehler={pruefungAnlegen.error} />

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="knopf-haupt"
                  disabled={pruefungAnlegen.isPending}
                >
                  {pruefungAnlegen.isPending ? 'Legt an …' : 'Prüfbogen erstellen'}
                </button>
                <button
                  type="button"
                  className="knopf-still"
                  onClick={() => setzePruefformular(false)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      {/* Gerätebestand */}
      <section>
        <div className="mb-2 flex items-center gap-3">
          <h2 className="text-xl font-bold">Gerätebestand</h2>
          {darfSchreiben(konto) && (
            <button
              type="button"
              className="knopf-zweit ml-auto"
              onClick={() => setzeGeraeteformular((o) => !o)}
            >
              {geraeteformular ? 'Abbrechen' : 'Gerät hinzufügen'}
            </button>
          )}
        </div>

        {geraeteformular && (
          <form
            className="karte mb-3 space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              const f = new FormData(e.currentTarget)
              const roh = Object.fromEntries(
                [...f.entries()].filter(([, w]) => String(w).trim() !== ''),
              )
              geraetAnlegen.mutate(
                {
                  ...roh,
                  baujahr: roh.baujahr ? Number(roh.baujahr) : undefined,
                  fallhoehe_cm: roh.fallhoehe_cm ? Number(roh.fallhoehe_cm) : undefined,
                  sortierung: (geraete.data?.length ?? 0) * 10,
                } as never,
                {
                  onSuccess: () => {
                    setzeGeraeteformular(false)
                    geraetAnlegen.reset()
                  },
                },
              )
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="bezeichnung">Bezeichnung</label>
                <input
                  id="bezeichnung"
                  name="bezeichnung"
                  required
                  placeholder="Doppelschaukel West"
                />
              </div>
              <div>
                <label htmlFor="geraetetyp">Gerätetyp</label>
                <select id="geraetetyp" name="geraetetyp" defaultValue="">
                  <option value="">— wählen —</option>
                  {(typen.data ?? []).map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="hersteller">Hersteller</label>
                <input id="hersteller" name="hersteller" />
              </div>
              <div>
                <label htmlFor="baujahr">Baujahr</label>
                <input id="baujahr" name="baujahr" inputMode="numeric" />
              </div>
              <div>
                <label htmlFor="seriennummer">Seriennummer</label>
                <input id="seriennummer" name="seriennummer" />
              </div>
              <div>
                <label htmlFor="fallschutzart">Fallschutz</label>
                <input id="fallschutzart" name="fallschutzart" placeholder="Fallschutzkies" />
              </div>
              <div>
                <label htmlFor="fallhoehe_cm">Fallhöhe in cm</label>
                <input id="fallhoehe_cm" name="fallhoehe_cm" inputMode="numeric" />
              </div>
            </div>

            <Fehlerhinweis fehler={geraetAnlegen.error} />

            <button type="submit" className="knopf-haupt" disabled={geraetAnlegen.isPending}>
              {geraetAnlegen.isPending ? 'Legt an …' : 'Gerät anlegen'}
            </button>
          </form>
        )}

        {geraete.isLoading ? (
          <Ladeanzeige />
        ) : (geraete.data ?? []).length === 0 ? (
          <Leerhinweis
            titel="Noch keine Geräte"
            text="Der Prüfbogen entsteht aus dem Gerätebestand."
          />
        ) : (
          <ul className="space-y-2">
            {(geraete.data ?? []).map((g) => (
              <li key={g.id} className="karte flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-semibold">
                    {g.bezeichnung}
                  </div>
                  <div className="text-base text-slate-700">
                    {[g.geraetetyp, g.hersteller, g.baujahr].filter(Boolean).join(' · ')}
                    {g.fallhoehe_cm ? ` · Fallhöhe ${g.fallhoehe_cm} cm` : ''}
                  </div>
                  {g.kennung && (
                    <div className="text-sm text-slate-600">
                      System-ID <span className="tabular-nums">{g.kennung}</span>
                    </div>
                  )}
                </div>
                {darfSchreiben(konto) && (
                  <button
                    type="button"
                    className="knopf-still px-3 text-base"
                    onClick={() => setzeGeraetBearbeiten(g)}
                  >
                    Bearbeiten
                  </button>
                )}
                {darfSchreiben(konto) && (
                  <button
                    type="button"
                    className="knopf-still px-3 text-base"
                    onClick={() => {
                      if (
                        window.confirm(
                          `${g.bezeichnung} ausmustern? Das Gerät verschwindet aus künftigen ` +
                            'Prüfbögen, bleibt in bisherigen Berichten aber erhalten.',
                        )
                      ) {
                        ausmustern.mutate({
                          id: g.id,
                          am: new Date().toISOString().slice(0, 10),
                        })
                      }
                    }}
                  >
                    Ausmustern
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Berichte */}
      <section>
        <h2 className="ueberschrift-akzent mb-3 text-xl">Berichte</h2>
        {(pruefungen.data ?? []).filter((x) => x.status === 'abgeschlossen').length === 0 ? (
          <Leerhinweis titel="Noch kein abgeschlossener Bericht" />
        ) : (
          <ul className="space-y-2">
            {(pruefungen.data ?? [])
              .filter((x) => x.status === 'abgeschlossen')
              .map((x) => (
                <li key={x.id} className="karte flex items-center gap-3">
                  <div className="flex-1">
                    <div className="font-semibold">
                      {x.berichtsnummer} · {PRUEFARTTEXT[x.pruefart]}
                    </div>
                    <div className="text-base text-slate-700">
                      {datumDeutsch(x.datum)} · {x.pruefer_name}
                    </div>
                  </div>
                  {/* Der Weg zu den Daten, nicht nur zum Dokument.
                      Hier stand allein der PDF-Knopf: wer nachsehen wollte,
                      welche Norm herangezogen wurde oder wie ein Befund
                      lautete, musste das Dokument heraussuchen. Der
                      Prüfbogen ist nach dem Abschluss lesbar – er war nur
                      von hier nicht erreichbar. */}
                  <Link to={`/pruefungen/${x.id}/bogen`} className="knopf-still px-3">
                    Ansehen
                  </Link>
                  <a
                    className="knopf-zweit px-3"
                    href={`/api/pruefungen/${x.id}/bericht.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    PDF
                  </a>
                </li>
              ))}
          </ul>
        )}
      </section>

      {platzBearbeiten && (
        <Bearbeiten
          titel="Spielplatz bearbeiten"
          untertitel={p.name}
          felder={PLATZFELDER}
          werte={p as unknown as Record<string, unknown>}
          laeuft={platzAendern.isPending}
          fehler={platzAendern.error}
          schliessen={() => {
            setzePlatzBearbeiten(false)
            platzAendern.reset()
          }}
          speichern={(aenderungen) =>
            platzAendern.mutate({ id: p.id, ...aenderungen } as never, {
              onSuccess: () => setzePlatzBearbeiten(false),
            })
          }
        />
      )}

      {geraetBearbeiten && (
        <Bearbeiten
          titel="Gerät bearbeiten"
          untertitel={geraetBearbeiten.bezeichnung}
          felder={GERAETEFELDER}
          werte={geraetBearbeiten as unknown as Record<string, unknown>}
          laeuft={geraetAendern.isPending}
          fehler={geraetAendern.error}
          schliessen={() => {
            setzeGeraetBearbeiten(null)
            geraetAendern.reset()
          }}
          speichern={(aenderungen) =>
            geraetAendern.mutate({ id: geraetBearbeiten.id, ...aenderungen } as never, {
              onSuccess: () => setzeGeraetBearbeiten(null),
            })
          }
        />
      )}
      {einplanen && (
        <Einplanen
          spielplatzId={platzId}
          spielplatzName={p.name}
          schliessen={() => setzeEinplanen(false)}
        />
      )}
    </div>
  )
}

const PLATZFELDER: Feld[] = [
  { name: 'name', beschriftung: 'Name', pflicht: true, breit: true },
  { name: 'baujahr', beschriftung: 'Baujahr', art: 'zahl' },
  { name: 'strasse', beschriftung: 'Straße', breit: true },
  { name: 'plz', beschriftung: 'PLZ' },
  { name: 'ort', beschriftung: 'Ort' },
  { name: 'flaeche_qm', beschriftung: 'Fläche in m²', art: 'zahl' },
  { name: 'altersgruppe', beschriftung: 'Altersgruppe', hinweis: 'z. B. 3–12 Jahre' },
  {
    name: 'routenziel',
    beschriftung: 'Routenziel für die Anfahrt',
    // Mehrzeilig: „hinter der Grundschule, Einfahrt über den Feldweg neben
    // dem Sportplatz, Schlüssel zur Schranke beim Bauhof" ist eine gültige
    // Wegbeschreibung und passt in keine Zeile.
    art: 'mehrzeilig',
    breit: true,
    hinweis:
      'Was ins Navigationsgerät eingegeben wird, wenn die Anschrift nicht zum Tor führt. Auch eine Beschreibung ist zulässig. Steht im Bericht und im Kalendertermin.',
  },
  {
    name: 'eigenkontrolle_tage',
    beschriftung: 'Eigenkontrolle alle … Tage',
    art: 'zahl',
    hinweis:
      'Abstand, in dem der Betreiber selbst nachsehen soll. Ein stark ' +
      'besuchter Platz an der Schule braucht das wöchentlich (7), ein ' +
      'ruhiger seltener. Leer lassen heißt: keine Erinnerung. Wirkt nur, ' +
      'wenn der Kunde dafür freigegeben ist.',
  },
  {
    name: 'bemerkung',
    beschriftung: 'Bemerkung',
    art: 'mehrzeilig',
    breit: true,
    hinweis: 'Interne Notiz. Erscheint nicht im Bericht.',
  },
]

const GERAETEFELDER: Feld[] = [
  { name: 'bezeichnung', beschriftung: 'Bezeichnung', pflicht: true, breit: true },
  { name: 'geraetetyp', beschriftung: 'Gerätetyp' },
  { name: 'hersteller', beschriftung: 'Hersteller' },
  { name: 'baujahr', beschriftung: 'Baujahr', art: 'zahl' },
  { name: 'seriennummer', beschriftung: 'Seriennummer' },
  { name: 'standort_hinweis', beschriftung: 'Standort', breit: true },
  { name: 'fallschutzart', beschriftung: 'Fallschutzart' },
  {
    name: 'fallhoehe_cm',
    beschriftung: 'Fallhöhe in cm',
    art: 'zahl',
    hinweis: 'Ab 60 cm ist Fallschutz nötig, ab 100 cm nach DIN EN 1177 geprüfter.',
  },
  { name: 'sortierung', beschriftung: 'Reihenfolge im Bogen', art: 'zahl' },
  { name: 'bemerkung', beschriftung: 'Bemerkung', art: 'mehrzeilig', breit: true },
]
